// src/store/app/selectors.js
import { createSelector } from '@reduxjs/toolkit'
import { ABLY_CLIENT_ID, CONNECTION_STATUS, DEVICE_TYPES, EMPTY_ARRAY } from '~/lib/constants'
import { selectQueries } from '~/store/api/selectors'

// eslint-disable-next-line no-unused-vars
// const logger = console.scope('app-selectors')

export const selectApp = state => state.app

// Connection-related selectors
export const selectAblyState = s => s.app.ably_state

export const selectPresence = s => s.app.presence

export const selectSessionPresence = s => s.app.session_presence

export const selectDCConnections = s => s.app.dc_connections

/** A `/output` screen is in the session: the one client that shows a map it has no project for. */
export const selectHasScreen = s => (s.app.dc_connections ?? []).some(c => c.device_type === DEVICE_TYPES.SCREEN)

export const selectClientMode = s => s.app.client_mode

export const selectIsRemote = s => s.app.client_mode === 'remote'

export const selectSourceDevice = s => s.app.source_device || null

export const selectOutputDevices = s => s.app.output_devices

export const selectPendingOutputs = s => s.app.pending_outputs

export const selectActiveDevice = s => s.app.source_device || null // deprecated alias

export const selectRTCState = s => s.app.rtc_state

export const selectDCState = s => s.app.dc_state

export const selectPCGeneration = s => s.app.pc_generation

export const selectRequestedControlMode = s => s.app.control_mode

export const selectVirtualCameraState = s => s.app.virtual_camera_state

export const selectIsConnected = s =>
	s.app.ably_state === CONNECTION_STATUS.CONNECTED &&
	s.app.rtc_state === CONNECTION_STATUS.CONNECTED &&
	s.app.dc_state === 'open'

export const selectIsSource = createSelector(selectSourceDevice, a => a === ABLY_CLIENT_ID)

export const selectIsActive = selectIsSource // deprecated alias

/** Does this client run automation and autoplay? Only one desktop per session does; with no server-named runner, every desktop runs its own. */
export const selectIsRunner = s => !s.app?.runner_supported || s.app.runner_device === ABLY_CLIENT_ID

/** Does the server name a runner? Only then are loop samples their own message and a peer's play a request. */
export const selectAutomationOnWire = s => !!s.app?.runner_supported

export const selectIsActiveOutput = createSelector(
	selectOutputDevices,
	selectDCState,
	s => s.app.output_devices_received,
	(outputDevices, dcState, received) =>
		dcState !== 'open' || (!received && outputDevices.length === 0) || outputDevices.includes(ABLY_CLIENT_ID)
)

export const selectIsStreaming = createSelector(
	selectIsConnected,
	selectIsActive,
	(connected, active) => connected && active
)

// Session-related selectors

export const selectSessionState = s => s.app.session_state

export const selectNetworkNotices = createSelector(
	s => s.app.network_quality,
	s => s.app.source_quality,
	(network_quality, source_quality) => ({ network_quality, source_quality })
)

export const selectControlMode = s => s.app.session_state.control_mode

export const selectServerUpscale = s => s.app.session_state.upscale

export const selectSessionLoadingState = createSelector(selectApp, app => app.session_loading)

// Project-related selectors
export const selectProjectId = s => s.app.project_id

export const selectIsTemplate = s => s.app.is_template

export const selectIsExamples = s => s.app.is_examples

export const selectProjectData = createSelector(
	selectProjectId,
	selectIsTemplate,
	selectQueries,
	(id, isTemplate, queries) => {
		if (!id) return null
		let data
		if (isTemplate) {
			data = queries[`getTemplate("${id}")`]?.data?.project
		} else {
			data = queries[`getProject("${id}")`]?.data
		}
		return data
	}
)

export const selectProject = createSelector(selectProjectData, data => data?.project)

export const selectPromptHistory = s => s.app.prompt.history || EMPTY_ARRAY
export const selectPrompt = s => s.app.prompt
export const selectPromptBrowse = s => s.app.prompt.browse

/**
 * The text a browsing field shows, or null when not browsing.
 *
 * Total by construction: an out-of-range index reads as the draft rather than `undefined`, which would blank a controlled input and flip it to uncontrolled.
 */
export const selectBrowsedPrompt = createSelector(selectPromptHistory, selectPromptBrowse, (history, browse) => {
	if (!browse) return null
	return browse.index >= 0 && browse.index < history.length ? history[browse.index] : browse.draft
})

/**
 * The furthest index the cursor may reach.
 *
 * ⚠️ The draft is a POSITION only when it differs from the newest entry. Browsing usually starts from a field showing that entry, so the draft holds the same string — and a slot for it is a step that displays nothing, with a button claiming a move is available. Entries are unique, but the draft is not an entry and nothing stops it duplicating one.
 */
const lastIndex = (history, draft) =>
	history.length && draft === history[history.length - 1] ? history.length - 1 : history.length

/**
 * Whether prev/next can move, given the text the field currently holds.
 *
 * The single derivation every prompt UI reads — desktop arrows and the mobile toolbar cannot disagree about which direction is available. Mirrors `stepPrompt`'s resolution of the un-browsed cursor: the entry the field is showing, else past the end.
 */
export const promptNav = (history, browse, text = '') => {
	const at = browse?.index ?? history.indexOf(text)
	const index = at >= 0 ? at : history.length
	const max = lastIndex(history, browse?.draft ?? text)
	return { index, canPrev: index > 0, canNext: !!browse && index < max }
}

export { lastIndex as promptLastIndex }

export const selectFPSStats = s => s.fps_stats
